/**
 * Client-side router and application shell.
 *
 * Responsibilities:
 *   - Build the <wa-page> shell once and reuse it across navigations.
 *   - Resolve the current path to a route and render its page into the main
 *     region, swapping only that region so the shell and nav persist.
 *   - Manage focus and the document title on navigation, which is what makes SPA
 *     navigation usable with a keyboard and screen reader (WCAG 2.2 AA).
 *
 * Navigation state (e.g. which profile to edit) is passed in memory through
 * `navigate(path, state)`, never through the URL. This is deliberate: §21 and
 * CLAUDE.md forbid credentials in URLs, and keeping ALL cross-page state out of
 * the URL means there is no page where that rule can be quietly broken. Opaque
 * ids would be tolerable in a URL, but "never in the URL" is a simpler rule to
 * hold the line on.
 */

import { routes, notFoundRoute } from "./routes.js";
import { createServices } from "../services/index.js";
import { renderShell, setActiveNav } from "./shell.js";
import { renderVerifyEmail } from "../pages/verify-email.js";
import { renderAcceptPolicies } from "../pages/accept-policies.js";
import { POLICY_VERSION } from "../config/policies.js";
import { el } from "../ui/dom.js";

/**
 * A page renderer. Receives the routing context and returns the content to place
 * in the main region.
 *
 * @callback PageRenderer
 * @param {RouteContext} context
 * @returns {Node}
 */

/**
 * @typedef {object} RouteContext
 * @property {import("../services/interfaces.js").Services} services
 * @property {import("../config/environment.js").KinCodeConfig} config
 * @property {(path: string, state?: Record<string, unknown>) => void} navigate
 * @property {Record<string, unknown>} state In-memory state passed to this route.
 */

/**
 * Start the router, taking over the given root element.
 *
 * @param {HTMLElement} root
 * @param {import("../config/environment.js").KinCodeConfig} config
 * @returns {void}
 */
export function startRouter(root, config) {
  const services = createServices(config);

  /** @type {Record<string, unknown>} */
  let pendingState = {};

  /**
   * @param {string} path
   * @param {Record<string, unknown>} [state]
   */
  function navigate(path, state = {}) {
    pendingState = state;
    if (path !== currentPath()) {
      window.history.pushState(null, "", path);
    }
    render();
  }

  const shell = renderShell();
  root.replaceChildren(shell.element);

  function render() {
    const path = currentPath();
    const route = routes[path] ?? notFoundRoute;

    // Client-side gate. The server is the real enforcer (CLAUDE.md); this keeps
    // the UI coherent and, crucially, never renders protected content to someone
    // who should not see it.
    if (route.requiresAuth) {
      // The auth backend resolves the persisted session asynchronously. Until it
      // has, we must not decide "signed out" — doing so would redirect a genuinely
      // signed-in visitor on every refresh, and could flash protected content
      // before a redirect. Show a neutral pending state and re-render on ready.
      if (!services.auth.isReady()) {
        renderAuthPending();
        return;
      }

      const account = services.auth.getCurrentAccount();
      if (!account) {
        pendingState = { returnTo: path };
        window.history.replaceState(null, "", "/sign-in");
        renderRoute(routes["/sign-in"] ?? notFoundRoute);
        setActiveNav(shell, "/sign-in");
        return;
      }

      // Signed in but unverified: hold at the verification gate rather than the
      // protected page. The gate offers resend and sign-out, so no one is trapped.
      if (!account.emailVerified) {
        renderVerifyGate();
        setActiveNav(shell, path);
        return;
      }

      // Updated policies: hold at the acceptance gate until the current versions are
      // accepted (§15). Skipped for an account already mid-deletion, so its owner can
      // still reach "Your data" to cancel or export. Dormant until a version is bumped.
      const outOfDate =
        (account.termsVersion ?? 0) < POLICY_VERSION.terms ||
        (account.privacyVersion ?? 0) < POLICY_VERSION.privacy;
      if (outOfDate && account.accountStatus !== "pendingDeletion") {
        renderPolicyGate();
        setActiveNav(shell, path);
        return;
      }

      // A verified account is being admitted to the admin journey, so start
      // attestation now rather than at start-up. This is the only place it is
      // triggered: the public retrieval page must never load the reCAPTCHA
      // script, which profiles the visitor. Safe to call on every render.
      services.startAttestation?.();
    }

    renderRoute(route);
    setActiveNav(shell, path);
  }

  /**
   * Neutral placeholder shown while the auth backend resolves the session. It
   * carries no protected content — only a status announcement.
   */
  function renderAuthPending() {
    document.title = "KinCode";
    const panel = el("div", { class: "kin-stack kin-pending", role: "status" }, [
      el("wa-spinner"),
      el("p", {}, ["Checking your sign-in…"]),
    ]);
    shell.setMain(panel, "Checking your sign-in");
  }

  /** Render the email-verification gate in place of a protected page. */
  function renderVerifyGate() {
    document.title = "Confirm your email — KinCode";
    /** @type {RouteContext} */
    const context = { services, config, navigate, state: pendingState };
    pendingState = {};
    shell.setMain(renderVerifyEmail(context), "Confirm your email address");
  }

  /** Render the policy re-acceptance gate in place of a protected page. */
  function renderPolicyGate() {
    document.title = "Review our policies — KinCode";
    /** @type {RouteContext} */
    const context = { services, config, navigate, state: pendingState };
    pendingState = {};
    shell.setMain(renderAcceptPolicies(context), "Please review our policies");
  }

  /** @param {import("./routes.js").RouteDefinition} route */
  function renderRoute(route) {
    /** @type {RouteContext} */
    const context = { services, config, navigate, state: pendingState };
    pendingState = {};

    document.title = route.title === "KinCode" ? "KinCode" : `${route.title} — KinCode`;

    const content = route.render(context);
    shell.setMain(content, route.title);
  }

  // Re-render on any auth-state change: a real backend resolving its initial
  // session, a sign-in, a sign-out, or a verification picked up by refresh().
  services.auth.subscribe(() => render());
  if (!services.auth.isReady()) {
    services.auth.whenReady().then(() => render());
  }

  window.addEventListener("popstate", () => {
    pendingState = {};
    render();
  });

  // Intercept in-app link clicks so navigation participates in client-side routing
  // instead of reloading the page. Any `[data-link]` element counts, not only bare
  // `<a>`: a `wa-button` with an `href` renders its own anchor in shadow DOM, so a
  // plain `a[data-link]` check would miss it and let it full-reload — which drops
  // in-memory state (and, on the mock backend, the session). A `mailto:` link
  // carries no `data-link`, so it is left for the browser.
  root.addEventListener("click", (event) => {
    const link = event.target instanceof Element ? event.target.closest("[data-link]") : null;
    if (!link) {
      return;
    }
    const href = link.getAttribute("href");
    if (!href || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) {
      return;
    }
    event.preventDefault();
    navigate(href);
  });

  render();
}

/** @returns {string} */
function currentPath() {
  return window.location.pathname || "/";
}
